import { AbstractControl, ValidatorFn } from '@angular/forms';
import { StringUtil } from '../util/StringUtil.model';
import { SpinnerService } from '@app/core/spinner/spinner.service';
import { EntityServiceInterface } from '../service/interfaces/EntityServiceInterface';
import { finalize } from 'rxjs/operators';
import { EntityInterface } from '../model/entity/interfaces/entityInterface.model';

export abstract class AbstractEntityValidator {

    private stopValidation = false;

    constructor(
        public spinner?: SpinnerService,
        public entityService?: EntityServiceInterface,
        public entityName?: string,
        public specialist?: string
    ) {

    }

    public format = (isList = false): ValidatorFn => (control: AbstractControl): { [key: string]: boolean } | null => {
            if (control.value) {
                let value: string;
                if (control.value instanceof String) {
                    value = control.value.toString().toUpperCase();
                } else {
                    value = String(control.value);
                    value = value.toUpperCase();
                }
                if (value !== '') {
                    let valueAux: string | Array<string> = value;
                    if (isList) {
                        valueAux = StringUtil.getSeparateArrayOfListString(value);
                    }
                    if (!this.validateFormatComplete(valueAux, isList)) {
                        return {format: true};
                    }
                }
            }
            return null;
        };

    public exists = (
            isSync = false,
            isList = false,
            cache = true,
            ok?: () => void,
            nok?: () => void,
            getEntity?: (entity: EntityInterface) => void,
            remove?: () => void): ValidatorFn => (control: AbstractControl): { [key: string]: boolean } | null => {
            if (control.value) {
                const value: string = control.value.toString().toUpperCase();
                if (value !== '') {
                    let valueAux: string | Array<string> = value;
                    if (isList) {
                        valueAux = StringUtil.getSeparateArrayOfListString(value);
                    }
                    if (this.validateFormatComplete(valueAux, isList)) {
                        this.validateExists(valueAux, control, isSync, isList, cache, ok, nok, getEntity);
                    } else {
                        return {format: true};
                    }
                } else {
                    if (remove) {
                        remove();
                    }
                }
            }
            return null;
        };

    validateExists(value: string | Array<string>, control: AbstractControl, isSync: boolean, isList: boolean, cache: boolean,
        ok: () => void, nok: () => void, getEntity: (entity: EntityInterface) => void): void {

        if (!this.stopValidation) {
            if (isSync) {
                this.spinner.startSpinner(`entity.${this.entityName.charAt(0).toLowerCase() + this.entityName.slice(1)}.actions.getting`);
            }
            if (isList) {
                const codeList: Array<string> = value as Array<string>;
                this.entityService.existsByListCodes(codeList, true, this.specialist)
                .pipe(finalize(() => {
                    if (isSync) {
                        this.spinner.stopSpinner();
                    }
                }))
                .subscribe(
                    (listOfNotFound: Array<string>) => {
                        if (listOfNotFound.length === 0) {
                            if (ok) {
                                ok();
                            }
                        } else {
                            this.setExistsError(control);
                            if (nok) {
                                nok();
                            }
                        }
                    },
                );

            } else {

                const code: string = value as string;

                this.entityService.existsByCode(code, true, this.specialist, cache)
                .pipe(finalize(() => {
                    if (isSync) {
                        this.spinner.stopSpinner();
                    }
                }))
                .subscribe(
                    (isExists: boolean) => {
                        if (isExists) {
                            if (ok) {
                                ok();
                            }
                            if (getEntity) {
                                this.entityService.findSimpleByCode(code, true, this.specialist, cache)
                                .pipe(finalize(() => {
                                }))
                                .subscribe(
                                    (entity: EntityInterface) => {
                                        getEntity(entity);
                                    }
                                );
                            }
                        } else {
                            this.setExistsError(control);
                            if (nok) {
                                nok();
                            }
                        }
                    }
                );
            }
        }
    }

    public notExists = (
        isSync = false,
        ok?: () => void,
        nok?: () => void,
        getEntity?: (entity: EntityInterface
    ) => void): ValidatorFn => (control: AbstractControl): { [key: string]: boolean } | null => {
            if (control.value) {
                const value: string = control.value.toString().toUpperCase();
                if (value !== '') {
                    if (this.validateFormatComplete(value, false) && control.value && (control.value as string).trim() !== '') {
                        if (isSync) {
                            this.spinner.startSpinner(`entity.${this.entityName.charAt(0).toLowerCase() + this.entityName.slice(1)}.actions.getting`);
                        }
                        const code: string = value ;

                        this.entityService.existsByCode(code, true, this.specialist)
                        .pipe(finalize(() => {
                            if (isSync) {
                                this.spinner.stopSpinner();
                            }
                        }))
                        .subscribe(
                            (isExists: boolean) => {
                                if (!isExists) {
                                    if (ok) {
                                        ok();
                                    }
                                } else {
                                    this.setNotExistsError(control);
                                    if (nok) {
                                        nok();
                                    }
                                    if (getEntity) {
                                        this.entityService.findByIdSimple(code, true, true)
                                        .pipe(finalize(() => {
                                        }))
                                        .subscribe(
                                            (entity: EntityInterface) => {
                                                getEntity(entity);
                                            }
                                        );
                                    }
                                }
                            }
                        );
                    } else {
                        return {format: true};
                    }
                }
            }
            return null;
        };

    // metodo para mostrar error si no está permitido algun valor de los que hay
    public notAllowed = (notAllowedList: Array<string>): ValidatorFn => (control: AbstractControl): { [key: string]: boolean } | null => {
            if (control.value) {
                const value: string = control.value.toString().toUpperCase();
                if (value !== '') {
                    if (notAllowedList.some((code: string) => value === code)) {
                        return {notAllowed: true};
                    }
                }
            }
            return null;
        };

    stop(): void {
        this.stopValidation = true;
    }
    start(): void {
        this.stopValidation = false;
    }

    private setExistsError(control: AbstractControl) {
        control.setErrors({notExists: true});
    }
    private setNotExistsError(control: AbstractControl) {
        control.setErrors({exists: true});
    }

    private validateFormatComplete(value: string | Array<string>, isList: boolean): boolean {
        if (isList) {
            const codeList: Array<string> = value as Array<string>;
            for (const code of codeList) {
                if (!this.validateFormat(code)) {
                    return false;
                }
            }
        } else {
            if (!this.validateFormat(value as string)) {
                return false;
            }
        }
        return true;
    }

    abstract validateFormat(value: string): boolean;


}
